import { selectHistoryDocuments, encounterMillis, encounterDate, compareEncounters, timestampMillis, clinicalSections } from './historyEvidence';
//standard full history processor, used for export
export const processHistory = (dataHistory, originalFieldKey) => {
  if (!dataHistory) return;

  const fullHistoryData = {};
  const records = selectHistoryDocuments(dataHistory);

  for (let i = records.length - 1; i >= 0; i--) {
    const record = records[i];
    const fieldData = record[originalFieldKey];
    const date = encounterDate(record.documentDate);
    for (const key in fieldData) {
      if (fieldData[key] !== "") {
        if (!fullHistoryData[key]) {
          fullHistoryData[key] = [];
        }
        fullHistoryData[key].push({ value: fieldData[key], date: date });
      }
    }
  }

  return fullHistoryData;
};

// return history data just for a specific section 
export const processHistoryData = (history, getKey) => {

  let historyData = [];
  let recordMap = new Map();

  history.slice().sort(compareEncounters).forEach((record) => {
    Object.keys(record).forEach((key) => {
      if (!["modifiedAt", "documentDate", "sourceDocumentId", "reviewState"].includes(key)) {
        let item = record[key];
        if (!item) return; // Skip null/undefined items
        let primaryValue = getKey(item);
        if (!primaryValue) return; // Skip items without a valid key
        const date = encounterMillis(record);
        if (!recordMap.has(primaryValue)) {
          recordMap.set(primaryValue, { ...item, date, sourceDocumentId: record.sourceDocumentId, isHistory: true });
        }
      }
    });
  });

  historyData = Array.from(recordMap.values()).map((item) => {
    return item;
  });

  return historyData;
};

/**
 * Collect all historical summary values for each row (matched by primary field).
 * Returns a Map keyed by primary-field value, where each value is an array of
 * { summary, date } objects sorted most-recent-first, capped at `maxEntries`.
 *
 * Used by the History Popup in EditableTableSection.
 *
 * @param {Array} history - Section-filtered records with encounter date and source identity
 * @param {Function} getKey - Extracts the primary-field value from an item
 * @param {number} maxEntries - Optional maximum historical entries per row (default unlimited)
 * @returns {Map<string, Array<{summary: string, date: number}>>}
 */
export const collectAllHistoryForRows = (history, getKey, maxEntries = Infinity) => {
  const historyMap = new Map();

  history.forEach((record) => {
    const date = encounterMillis(record);

    Object.keys(record).forEach((key) => {
      if (["modifiedAt", "documentDate", "sourceDocumentId", "reviewState"].includes(key)) return;
      const item = record[key];
      if (!item || typeof item !== "object") return;
      const primaryValue = getKey(item);
      if (!primaryValue) return;
      const summary = item.summary;
      if (!summary || (typeof summary === "string" && summary.trim() === "")) return;

      if (!historyMap.has(primaryValue)) {
        historyMap.set(primaryValue, []);
      }
      historyMap.get(primaryValue).push({ summary, date, sourceDocumentId: record.sourceDocumentId, reviewState: record.reviewState });
    });
  });

  // Sort each entry list by date descending and cap at maxEntries
  historyMap.forEach((entries, key) => {
    entries.sort((a, b) => (b.date || 0) - (a.date || 0));
    if (entries.length > maxEntries) {
      historyMap.set(key, entries.slice(0, maxEntries));
    }
  });

  return historyMap;
};

// Helper function to select the best item from a collection of items with the same key
const selectBestItem = (items, safeCurrentData, safeHistoryData) => {
  // 1. Prefer a current item with a non-empty summary
  const currentWithSummary = items.find((item) => 
    safeCurrentData.includes(item) && item.summary && item.summary.trim() !== ""
  );
  if (currentWithSummary) return currentWithSummary;

  // 2. Otherwise, prefer the most recent history item with a non-empty summary
  const historyWithSummary = items
    .filter((item) => 
      safeHistoryData.includes(item) && item.summary && item.summary.trim() !== ""
    )
    .sort((a, b) => (b.date || 0) - (a.date || 0))[0];
  if (historyWithSummary) return historyWithSummary;

  // 3. Otherwise, use any current item
  const anyCurrent = items.find((item) => safeCurrentData.includes(item));
  if (anyCurrent) return anyCurrent;

  // 4. Otherwise, use any history item
  const anyHistory = items.find((item) => safeHistoryData.includes(item));
  return anyHistory;
};

// Helper function to combine and deduplicate data
const combineHistoryData = (section, safeCurrentData, safeHistoryData, getKey) => {
  if (section.allowMultiples) {
    return [...safeCurrentData, ...safeHistoryData];
  }

  const uniqueDataMap = new Map();
  const allItemsByKey = new Map();

  [...safeCurrentData, ...safeHistoryData].forEach((item) => {
    const key = getKey(item);
    if (!allItemsByKey.has(key)) allItemsByKey.set(key, []);
    allItemsByKey.get(key).push(item);
  });

  allItemsByKey.forEach((items, key) => {
    const bestItem = selectBestItem(items, safeCurrentData, safeHistoryData);
    if (bestItem) {
      uniqueDataMap.set(key, bestItem);
    }
  });

  return Array.from(uniqueDataMap.values());
};

// Helper function to sort combined data
const sortCombinedData = (combinedData, section) => {
  switch (section.sort) {
    case "alphabetical":
      combinedData.sort((a, b) => {
        const fieldA = (a[section.primaryField] || "").toLowerCase();
        const fieldB = (b[section.primaryField] || "").toLowerCase();
        return fieldA.localeCompare(fieldB);
      });
      break;
    case "current":
      combinedData.sort((a, b) => {
        if (a.current !== b.current) return b.current ? 1 : -1;
        return (a[section.primaryField] || "").toLowerCase().localeCompare(
          (b[section.primaryField] || "").toLowerCase()
        );
      });
      break;
    case "custom":
      if (Array.isArray(section.sortOrder)) {
        combinedData.sort((a, b) => {
          const sortOrder = section.sortOrder;
          const indexA = sortOrder.indexOf(a[section.primaryField] || "");
          const indexB = sortOrder.indexOf(b[section.primaryField] || "");
          const maxIndex = sortOrder.length;
          return (indexA === -1 ? maxIndex : indexA) - (indexB === -1 ? maxIndex : indexB);
        });
      }
      break;
    case "custom-data":
      if (Array.isArray(section.sortOrder)) {
        combinedData.sort((a, b) => {
          const sortOrder = section.sortOrder;
          const indexA = sortOrder.indexOf(a.originalName || "");
          const indexB = sortOrder.indexOf(b.originalName || "");
          const maxIndex = sortOrder.length;
          return (indexA === -1 ? maxIndex : indexA) - (indexB === -1 ? maxIndex : indexB);
        });
      }
      break;
    default:
      break;
  }
};

// process history by section with sorting for tables, currentData is one record, historyData, is many
export const processHistorySection = (section, currentData, historyData, runSort = false) => {

  if (!section?.primaryField) return [{}];

  // Convert object format to array if needed (for current data)
  let processedCurrentData = currentData;
  if (currentData && !Array.isArray(currentData) && typeof currentData === 'object') {
    // Get all keys except metadata fields
    const keys = Object.keys(currentData);
    const dataKeys = keys.filter(key => !['modifiedAt', 'processingStatus'].includes(key));
    
    if (dataKeys.length > 0) {
      // Convert object to array format (works for both numbered and named keys)
      processedCurrentData = dataKeys
        .map(key => ({
          ...currentData[key],
          originalKey: key // Preserve the original key if needed
        }))
        .filter(item => item && typeof item === 'object'); // Ensure valid items
    }
  }

  const safeCurrentData = Array.isArray(processedCurrentData) ? processedCurrentData.filter(Boolean) : [];
  const safeHistoryData = Array.isArray(historyData) ? historyData.filter(Boolean) : [];
  const getKey = (item) => (item ? item[section.primaryField] || item.id || "" : "");

  const combinedData = combineHistoryData(section, safeCurrentData, safeHistoryData, getKey);

  if (runSort) {
    sortCombinedData(combinedData, section);
  }

  // For first processing of primary sections, allow 1 empty field. For subsequent processing, prevent duplicates.
  if (combinedData.length === 0) {
    const isFirstProcessing = !historyData || historyData.length === 0;
    const isPrimarySection = section.editorStyle === "table" || section.primaryField;
    
    // Only create empty placeholder on first processing for primary sections
    if (isFirstProcessing && isPrimarySection) {
      return [{}];
    }
    
    // For subsequent processing or non-primary sections, return empty array
    return [];
  }

  return combinedData;
};

// currently used for fetching the most Recent Therapy Record
export const mostRecentTherapyHistory = documentHistory => selectHistoryDocuments(documentHistory).find(doc => doc.therapyType);
export const mostRecentDocument = (patientDocuments, field) => selectHistoryDocuments(patientDocuments).find(doc => doc[field]) || null;
// Legacy summaries remain readable without backfilling provenance or approvals.
export const isSavedHistoryDocument = doc => !!doc && !doc.isArchived && !doc.isDeleted && !doc.deletedAt
  && ['completed', 'approved', 'exported'].includes(doc.status);
export const mostRecentHistory = patientDocuments => (patientDocuments || [])
  .filter(doc => isSavedHistoryDocument(doc) && doc.history_summaries)
  .sort((a, b) => (timestampMillis(b.createdAt) || 0) - (timestampMillis(a.createdAt) || 0))[0] || null;

// Helper function to determine trend based on change and severity threshold
const determineSeverityTrend = (change, currentSeverity) => {
  if (change > 0.2) {
    return 'getting worse';
  } else if (change < -0.2) {
    return 'getting better';
  } else {
    // For stable conditions, use different wording based on severity
    return currentSeverity > 3 ? 'trend unchanged' : 'staying stable';
  }
};

// returns a severity trend analysis
export const analyzeSeverityTrend = (patientDocuments, fieldPath) => {
  const savedSummary = fieldPath.endsWith('_history_summary');
  patientDocuments = savedSummary ? (patientDocuments || []).filter(isSavedHistoryDocument) : selectHistoryDocuments(patientDocuments);
  // Helper function to extract field data from a document
  const extractFieldData = (doc, fieldPath) => {
    const pathParts = fieldPath.split('.');
    let current = doc;
    
    for (const part of pathParts) {
      if (current && typeof current === 'object') {
        current = current[part];
      } else {
        return null;
      }
    }
    return current;
  };

  // Helper function to get date from document
  // Match the earlier saved-summary selection order. Source-note table chronology
  // continues to use encounter dates independently.
  const getDocumentDate = doc => {
    const ms = savedSummary
      ? timestampMillis(doc.modifiedAt) ?? timestampMillis(doc.createdAt) ?? encounterMillis(doc)
      : encounterMillis(doc);
    return ms === null ? null : new Date(ms);
  };

  // Get the most recent document with the field
  const mostRecentDoc = patientDocuments
    .filter(doc => extractFieldData(doc, fieldPath))
    .sort((a, b) => {
      const dateA = getDocumentDate(a);
      const dateB = getDocumentDate(b);
      if (!dateA || !dateB) return 0;
      return dateB - dateA; // Most recent first
    })[0];

  if (!mostRecentDoc) {
    return null;
  }

  const currentFieldData = extractFieldData(mostRecentDoc, fieldPath);
  if (!currentFieldData) {
    return currentFieldData;
  }

  // Collect historical severity data for each item by originalName
  const historicalData = {};
  
  patientDocuments.forEach(doc => {
    const fieldData = extractFieldData(doc, fieldPath);
    const docDate = getDocumentDate(doc);
    
    if (!fieldData || !docDate) return;
    
    Object.values(fieldData).forEach(item => {
      if (item && typeof item === 'object' && item.severityRank !== undefined && item.originalName) {
        if (!historicalData[item.originalName]) {
          historicalData[item.originalName] = [];
        }
        
        historicalData[item.originalName].push({
          date: docDate,
          severityRank: item.severityRank
        });
      }
    });
  });

  // Add trend analysis to current field data
  const enhancedFieldData = {};
  
  Object.entries(currentFieldData).forEach(([key, item]) => {
    if (!item || typeof item !== 'object') return;
    
    const history = historicalData[item.originalName] || [];
    
    // Sort by date (oldest first)
    history.sort((a, b) => a.date - b.date);
    
    // Calculate trend and average
    let severityRankTrend = item.severityRank > 3 ? 'trend unchanged' : 'staying stable';
    let severityRankAverage = item.severityRank;
    
    if (history.length > 1) {
      const severityValues = history.map(h => h.severityRank);
      const firstValue = severityValues[0];
      const lastValue = severityValues[severityValues.length - 1];
      
      // Calculate trend using new logic
      const change = lastValue - firstValue;
      severityRankTrend = determineSeverityTrend(change, item.severityRank);
      
      // Calculate average
      severityRankAverage = Math.round(
        (severityValues.reduce((sum, val) => sum + val, 0) / severityValues.length) * 10
      ) / 10;
    }
    
    enhancedFieldData[key] = {
      ...item,
      severityRankTrend,
      severityRankAverage
    };
  });

  return enhancedFieldData;
};

// returns overall severity trend analysis across all severity-ranked items in patient documents
export const analyzeOverallSeverityRank = (patientDocuments) => {
  patientDocuments = selectHistoryDocuments(patientDocuments);
  // Helper function to get date from document (reusing from analyzeSeverityTrend)
  const getDocumentDate = doc => encounterMillis(doc) === null ? null : new Date(encounterMillis(doc));

  // Helper function to recursively find all objects with severityRank
  const extractSeverityItems = (obj, docDate) => {
    const items = [];
    
    const traverse = (current) => {
      if (current && typeof current === 'object') {
        if (current.severityRank !== undefined && 
            current.severityRank !== null && 
            !isNaN(current.severityRank) && 
            typeof current.severityRank === 'number') {
          items.push({
            severityRank: current.severityRank,
            date: docDate,
            originalName: current.originalName || 'unknown'
          });
        }
        
        // Recursively check all properties
        Object.values(current).forEach(value => {
          if (value && typeof value === 'object') {
            traverse(value);
          }
        });
      }
    };
    
    traverse(obj);
    return items;
  };

  // Collect all severity data across all documents
  const allSeverityData = [];
  
  patientDocuments.forEach(doc => {
    const docDate = getDocumentDate(doc);
    if (!docDate) return;
    
    // Extract all severity items from this document
    const severityItems = extractSeverityItems(clinicalSections(doc), docDate);
    allSeverityData.push(...severityItems);
  });

  if (allSeverityData.length === 0) {
    return {
      overallSeverityRank: null,
      overallSeverityTrend: 'stable',
      overallSeverityAverage: null,
      totalItems: 0,
      dateRange: null
    };
  }

  // Sort by date (oldest first)
  allSeverityData.sort((a, b) => a.date - b.date);

  // Group data by date to calculate daily averages
  const dailyAverages = {};
  
  allSeverityData.forEach(item => {
    const dateKey = item.date.toISOString().slice(0, 10);
    if (!dailyAverages[dateKey]) {
      dailyAverages[dateKey] = {
        date: item.date,
        severityValues: []
      };
    }
    dailyAverages[dateKey].severityValues.push(item.severityRank);
  });

  // Calculate average severity for each date
  const dailyData = Object.values(dailyAverages).map(day => {
    const validSeverityValues = day.severityValues.filter(val => 
      val !== null && val !== undefined && !isNaN(val) && typeof val === 'number'
    );
    
    if (validSeverityValues.length === 0) return null;
    
    return {
      date: day.date,
      averageSeverity: validSeverityValues.reduce((sum, val) => sum + val, 0) / validSeverityValues.length
    };
  }).filter(day => day !== null).sort((a, b) => a.date - b.date);

  // Calculate overall metrics
  const allSeverityValues = allSeverityData
    .map(item => item.severityRank)
    .filter(val => val !== null && val !== undefined && !isNaN(val) && typeof val === 'number');
  
  if (allSeverityValues.length === 0 || dailyData.length === 0) {
    return {
      overallSeverityRank: null,
      overallSeverityTrend: 'stable',
      overallSeverityAverage: null,
      totalItems: 0,
      dateRange: null
    };
  }
  
  const overallAverage = allSeverityValues.reduce((sum, val) => sum + val, 0) / allSeverityValues.length;
  
  // Current severity (most recent document's average)
  const mostRecentSeverity = dailyData[dailyData.length - 1].averageSeverity;
  
  // Calculate trend
  let overallTrend = mostRecentSeverity > 3 ? 'trend unchanged' : 'staying stable';
  
  if (dailyData.length > 1) {
    const firstAverage = dailyData[0].averageSeverity;
    const lastAverage = dailyData[dailyData.length - 1].averageSeverity;
    
    const change = lastAverage - firstAverage;
    overallTrend = determineSeverityTrend(change, mostRecentSeverity);
  }
  
  return {
    overallSeverityRank: Math.round(mostRecentSeverity * 10) / 10,
    overallSeverityTrend: overallTrend,
    overallSeverityAverage: Math.round(overallAverage * 10) / 10,
    totalItems: allSeverityValues.length, // Use filtered count
    dateRange: {
      start: dailyData[0].date,
      end: dailyData[dailyData.length - 1].date
    },
    dailyAverages: dailyData
  };
};

// determines contact frequency based on document dates
export const determineContactFrequency = (patientDocuments) => {
  if (!patientDocuments || patientDocuments.length === 0) {
    return {
      averageDaysBetween: null,
      frequency: 'infrequently'
    };
  }

  // Filter documents with valid documentDate and sort by date
  const validDocuments = patientDocuments
    .filter(doc => doc.documentDate && typeof doc.documentDate === 'string')
    .map(doc => ({
      ...doc,
      parsedDate: new Date(doc.documentDate)
    }))
    .filter(doc => !isNaN(doc.parsedDate.getTime()))
    .sort((a, b) => a.parsedDate - b.parsedDate);

  const firstDocDate = validDocuments.length > 0 ? validDocuments[0].parsedDate : null;
  const lastDocDate = validDocuments.length > 0
    ? validDocuments[validDocuments.length - 1].parsedDate
    : null;

  // Need at least two real visits to derive a visit-to-visit cadence.
  if (validDocuments.length < 2) {
    return {
      averageDaysBetween: null,
      frequency: 'infrequent',
      firstDocumentDate: firstDocDate,
      lastDocumentDate: lastDocDate,
      nextExpectedDate: null,
    };
  }

  // Calculate days between consecutive *real visits* only.
  // NOTE: we deliberately do NOT inject `today` as a synthetic contact point.
  // Doing so conflated cadence with recency — a dormant patient's huge
  // last-visit→today gap inflated the average and pushed them into the
  // "Expected This Week" list forever. Cadence is now visit-to-visit;
  // "overdue/dormant" is handled separately by the consumer.
  const daysBetweenContacts = [];
  for (let i = 1; i < validDocuments.length; i++) {
    const daysDifference = (validDocuments[i].parsedDate - validDocuments[i - 1].parsedDate) / (1000 * 60 * 60 * 24);
    daysBetweenContacts.push(daysDifference);
  }

  // Calculate average days between contacts
  const averageDaysBetween = Math.round(
    daysBetweenContacts.reduce((sum, days) => sum + days, 0) / daysBetweenContacts.length
  );

  // Determine frequency category based on average
  let frequency = 'infrequent';
  if (averageDaysBetween <= 2) {
    frequency = 'daily';
  } else if (averageDaysBetween <= 10) {
    frequency = 'weekly';
  } else if (averageDaysBetween <= 45) {
    frequency = 'monthly';
  }

  const nextExpectedDate = (lastDocDate && averageDaysBetween)
    ? new Date(lastDocDate.getTime() + averageDaysBetween * 24 * 60 * 60 * 1000)
    : null;

  return {
    averageDaysBetween,
    frequency,
    firstDocumentDate: firstDocDate,
    lastDocumentDate: lastDocDate,
    nextExpectedDate,
  };
};
